<template>
  <el-card shadow="never" class="home-dashboard__card home-donut">
    <div slot="header" class="home-donut__header">
      <span class="home-dashboard__card-title">Distribuição por estado</span>
      <PeriodSwitch v-model="days" :options="dayOptions" @change="fetch" />
    </div>

    <div v-loading="loading" class="home-donut__body">
      <template v-if="states.length > 0">
        <div class="home-donut__content">
          <e-chart :option="option" height="240px" class="home-donut__chart" @chart-click="onSliceClick"
            @chart-mouseover="onSliceHover" @chart-mouseout="hoverSlice = null" />
          <!-- UFs clicáveis: cobre as fatias pequenas demais para clicar -->
          <div class="home-donut__ufs">
            <button v-for="row in states" :key="row.state" type="button" class="home-donut__uf"
              :class="{ 'home-donut__uf--excluded': excluded.includes(row.state) }"
              :title="`${row.state}: ${row.total.toLocaleString('pt-BR')} — clique para ${excluded.includes(row.state) ? 'voltar' : 'remover'}`"
              @click="toggleState(row.state)">
              <span class="home-donut__uf-dot"
                :style="{ background: isMalha(row.state) ? '#f6921e' : '#22418d' }"></span>
              {{ row.state }}
            </button>
          </div>
        </div>
        <div v-if="excluded.length === 0" class="home-donut__hint">
          Todos os estados = 100% — clique numa fatia ou numa UF para remover do cálculo
        </div>
        <div v-else class="home-donut__remaining">
          {{ excluded.length }} estado(s) fora do cálculo
          <el-button type="text" size="mini" class="home-donut__restore" @click="excluded = []">Restaurar</el-button>
        </div>
      </template>
      <div v-else-if="!loading" class="home-donut__empty">{{ $t("sem-postagens") }}</div>
    </div>
  </el-card>
</template>

<script>
// Rosca interativa por ESTADO: todos os estados somam 100%; clicar numa fatia
// tira o estado do cálculo e os percentuais se redistribuem entre os que
// ficaram (a linha abaixo mostra quanto sobrou do total original). Estados da
// malha em laranja, como no mapa. Mesmo endpoint do mapa de estados.
import { PrepostCountService } from "@/services/customers/prepost.count.services";
import { MALHA_STATES } from "@/xpost/helpers/prepost-status";
import EChart from "@/components/Charts/EChart.vue";
import PeriodSwitch, { DAY_OPTIONS } from "./PeriodSwitch.vue";

export default {
  name: "HomeStateDonut",
  components: { EChart, PeriodSwitch },
  data() {
    return {
      days: 30,
      dayOptions: DAY_OPTIONS,
      loading: false,
      total: 0,
      states: [],
      excluded: [],
      hoverSlice: null,
    };
  },
  computed: {
    activeStates() {
      return this.states.filter((row) => !this.excluded.includes(row.state));
    },
    remainingTotal() {
      return this.activeStates.reduce((sum, row) => sum + row.total, 0);
    },
    remainingPct() {
      return this.total ? ((this.remainingTotal / this.total) * 100).toFixed(1) : "0.0";
    },
    option() {
      // Tons alternados dentro de cada família para fatias vizinhas não se
      // fundirem: laranja = malha, azul = demais estados
      let malhaIndex = 0;
      let otherIndex = 0;
      const alphas = [1, 0.75, 0.55, 0.88, 0.65, 0.45];
      const data = this.activeStates.map((row) => {
        const malha = MALHA_STATES.includes(row.state);
        const alpha = alphas[(malha ? malhaIndex++ : otherIndex++) % alphas.length];
        return {
          name: row.state,
          value: row.total,
          itemStyle: {
            color: malha ? `rgba(246, 146, 30, ${alpha})` : `rgba(34, 65, 141, ${alpha})`,
            borderColor: "#fff",
            borderWidth: 1,
          },
        };
      });
      return {
        title: {
          text: this.hoverSlice ? `${this.hoverSlice.percent}%` : `${this.remainingPct}%`,
          subtext: this.hoverSlice
            ? `${this.hoverSlice.name} - ${this.hoverSlice.value.toLocaleString("pt-BR")} postagens`
            : `${this.remainingTotal.toLocaleString("pt-BR")} postagens`,
          left: "center",
          top: "38%",
          itemGap: 4,
          textStyle: { fontSize: 24, fontWeight: 700, color: "#303133" },
          subtextStyle: { fontSize: 11, color: "#909399" },
        },
        series: [
          {
            type: "pie",
            radius: ["55%", "80%"],
            center: ["50%", "50%"],
            label: { show: false },
            emphasis: {
              label: { show: false },
              itemStyle: { shadowBlur: 8, shadowColor: "rgba(0, 0, 0, 0.15)" },
            },
            labelLine: { show: false },
            data,
          },
        ],
      };
    },
  },
  methods: {
    isMalha(uf) {
      return MALHA_STATES.includes(uf);
    },
    // Clique alterna: remove do cálculo ou traz de volta
    toggleState(uf) {
      const index = this.excluded.indexOf(uf);
      if (index === -1) {
        this.excluded.push(uf);
      } else {
        this.excluded.splice(index, 1);
      }
    },
    onSliceHover(params) {
      if (params && params.name) {
        this.hoverSlice = { name: params.name, value: params.value, percent: params.percent };
      }
    },
    onSliceClick(params) {
      if (params && params.name) {
        this.toggleState(params.name);
      }
    },
    fetch() {
      const vm = this;
      vm.loading = true;
      PrepostCountService.state(vm.days)
        .then((response) => {
          vm.states = (response.states || []).slice().sort((a, b) => b.total - a.total);
          vm.total = +response.total || 0;
          vm.excluded = [];
        })
        .catch((error) => {
          vm.$message({ type: "error", message: error });
        })
        .finally(() => {
          vm.loading = false;
        });
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>

<style>
.home-donut__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.home-donut__content {
  display: flex;
  align-items: center;
}

.home-donut__chart {
  flex: 1 1 200px;
  min-width: 180px;
}

/* UFs em colunas compactas ao lado da rosca */
.home-donut__ufs {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-height: 228px;
  margin-left: 6px;
}

.home-donut__uf {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 1px 6px 1px 0;
  font-size: 11px;
  line-height: 1.7;
  color: #606266;
  cursor: pointer;
  transition: opacity 0.15s ease, color 0.15s ease;
}

.home-donut__uf:hover {
  color: #22418d;
}

.home-donut__uf--excluded {
  opacity: 0.35;
  text-decoration: line-through;
}

.home-donut__uf-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 4px;
}

.home-donut__hint {
  text-align: center;
  font-size: 11px;
  color: var(--me-text-secondary);
  margin-top: 4px;
}

.home-donut__remaining {
  text-align: center;
  font-size: 12px;
  color: var(--me-text-regular);
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}

.home-donut__restore {
  padding: 0;
  margin-left: 6px;
}

.home-donut__empty {
  color: var(--me-text-secondary);
  font-size: 13px;
  padding: 8px 0;
}
</style>
